import apiClient from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import type {
  ApplicationApplicantStatusPayload,
  ExpireApplicationPayload,
  OverrideApplicantStatusPayload,
  ResetApplicationPayload,
} from '../types/services'
import { throwServiceError } from './serviceHelpers'

/** Per-applicant status update — POST /api/application/:applicantId (Angular approvedApplicant). */
export async function updateApplicationStatus(
  applicantId: string | number,
  requestData: ApplicationApplicantStatusPayload,
): Promise<void> {
  try {
    await apiClient.post(`/api/application/${applicantId}`, requestData)
  } catch (error) {
    throwServiceError(error, 'Failed to update application status.')
  }
}

/** Approve applicant — POST /api/application/:applicantId (Angular approvedApplicant). */
export async function approveApplication(
  applicantId: string | number,
  requestData: ApplicationApplicantStatusPayload,
): Promise<void> {
  try {
    await apiClient.post(`/api/application/${applicantId}`, requestData)
  } catch (error) {
    throwServiceError(error, 'Failed to approve application.')
  }
}

/** Override applicant status (Override Status modal). */
export async function overrideApplicantStatus(
  applicantId: string | number,
  requestData: OverrideApplicantStatusPayload | Record<string, unknown>,
): Promise<void> {
  try {
    await apiClient.post(API_ENDPOINTS.UPDATE_APPLICANT_STATUS(applicantId), requestData)
  } catch (error) {
    throwServiceError(error, 'Failed to override applicant status.')
  }
}

/** Expire application — POST /api/application/:applicantId with applicant_status EXPIRED. */
export async function expireApplication(
  applicantId: string | number,
  requestData: ExpireApplicationPayload = {},
): Promise<void> {
  try {
    await apiClient.post(`/api/application/${applicantId}`, {
      applicant_status: 'EXPIRED',
      ...requestData,
    })
  } catch (error) {
    throwServiceError(error, 'Failed to expire application.')
  }
}

/** Reset application (unexpire/uncancel) — POST /api/reset_application/:id. */
export async function resetApplication(
  applicationId: string | number,
  requestData: ResetApplicationPayload,
): Promise<void> {
  try {
    await apiClient.post(`/api/reset_application/${applicationId}`, requestData)
  } catch (error) {
    throwServiceError(error, 'Failed to reset application.')
  }
}

/** Underwriting check-in/out — POST /api/underwriting_check/:id/checkout|checkin (Angular checkoutinProcess). */
export async function underwritingCheckProcess(
  applicantId: string | number,
  action: 'checkout' | 'checkin',
): Promise<void> {
  try {
    await apiClient.post(`/api/underwriting_check/${applicantId}/${action}`)
  } catch (error) {
    throwServiceError(error, 'Failed to update underwriting check-in/out.')
  }
}

/** Legacy checkout — prefer {@link underwritingCheckProcess} for Angular parity. */
export async function checkoutinProcess(
  applicantId: string | number,
  status: string,
): Promise<void> {
  try {
    await apiClient.post(`/api/checkoutin_process/${applicantId}`, {
      check_in: status,
    })
  } catch (error) {
    throwServiceError(error, 'Failed to update Check-In/Out status.')
  }
}

/** Un-suspend applicant account. */
export async function unsuspendAccount(applicantId: string | number): Promise<void> {
  try {
    await apiClient.post(`/api/update_previous_default/${applicantId}`, {
      account_suspended: null,
    })
  } catch (error) {
    throwServiceError(error, 'Failed to unsuspend account.')
  }
}

/** Angular Notes tab executive overrides → `POST /api/update_previous_default/:applicantId` */
export async function updateApplicantPreviousDefaultOverrides(
  applicantId: string | number,
  body: Record<string, string | null>,
): Promise<void> {
  try {
    await apiClient.post(`/api/update_previous_default/${applicantId}`, body)
  } catch (error) {
    throwServiceError(error, 'Failed to update applicant overrides.')
  }
}

/** Make applicant primary (swap primary and co-applicant). */
export async function makePrimaryApplicant(applicantId: string | number): Promise<void> {
  try {
    await apiClient.post(API_ENDPOINTS.SWAP_APPLICANT(applicantId), {})
  } catch (error) {
    throwServiceError(error, 'Failed to make applicant primary.')
  }
}
